import { OptimizerObjectiveSchema, OptimizerVariableSchema, type OptimizerVariable } from '@trajectory/contracts';
import { CheckboxField, SelectField } from '@trajectory/ui';
import { Loader2, Play, Wand2 } from 'lucide-react';
import { useState } from 'react';
import { ConvergenceChart } from '@/charts/ConvergenceChart';
import { PanelLearningBanner } from '@/components/PanelLearningBanner';
import { TermHelp } from '@/components/TermHelp';
import { useOptimizerJob, useStartOptimizer } from '@/hooks/useOptimizer';
import { useMissionStore } from '@/stores/missionStore';
import { useUiStore } from '@/stores/uiStore';

const DEFAULT_VARIABLES: OptimizerVariable[] = ['pitchStartAltitude', 'turnEndAltitude', 'turnExponent'];

const VARIABLE_LABELS: Record<OptimizerVariable, string> = {
  pitchStartAltitude: 'Pitch-over start altitude',
  turnEndAltitude: 'Gravity-turn end altitude',
  turnExponent: 'Turn exponent',
  closedLoopAltitude: 'Closed-loop guidance altitude',
  verticalSpeedTimeConstant: 'Vertical-speed time constant',
  azimuth: 'Launch azimuth',
};

/**
 * Deterministic differential-evolution / Nelder–Mead search over the ascent
 * programme — labelled "Trajectory Optimizer" rather than "AI optimizer"
 * throughout the app because that is what actually runs here, not a language
 * model. See `docs/09-mathematical-models.md`.
 */
export function OptimizerPanel() {
  const missionId = useMissionStore((state) => state.id);
  const [objective, setObjective] = useState(OptimizerObjectiveSchema.options[0]);
  const [variables, setVariables] = useState<Set<OptimizerVariable>>(new Set(DEFAULT_VARIABLES));
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobMissionId, setJobMissionId] = useState<string | null>(null);

  const startOptimizer = useStartOptimizer();
  const job = useOptimizerJob(jobId);

  const toggleVariable = (variable: OptimizerVariable, checked: boolean) => {
    setVariables((current) => {
      const next = new Set(current);
      if (checked) next.add(variable);
      else next.delete(variable);
      return next;
    });
  };

  const handleRun = async () => {
    if (!missionId || variables.size === 0) return;
    const requestedMissionId = missionId;
    try {
      const result = await startOptimizer.mutateAsync({
        missionId: requestedMissionId,
        objective,
        variables: [...variables],
      });
      setJobMissionId(requestedMissionId);
      setJobId(result.id);
    } catch (error) {
      useUiStore.getState().notify({
        kind: 'danger',
        title: 'Optimizer could not start',
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const handleApply = () => {
    if (!job.data?.resultingProgram) return;
    if (!missionId || missionId !== jobMissionId) {
      useUiStore.getState().notify({
        kind: 'warning',
        title: 'Optimizer result belongs to another mission',
        message:
          'Return to the mission that started this optimization or run a new optimization for the selected mission.',
      });
      return;
    }
    useMissionStore.getState().patch({ ascentProgram: job.data.resultingProgram });
    useUiStore.getState().notify({ kind: 'success', title: 'Ascent programme applied', message: 'Re-run the simulation to see it.' });
  };

  if (!missionId) {
    return <div className="flex h-full items-center justify-center p-4 text-center text-xs text-faint">Select a mission first.</div>;
  }

  const running = job.data?.status === 'queued' || job.data?.status === 'running';

  return (
    <div className="flex h-full flex-col gap-3 overflow-auto p-3 text-sm">
      <PanelLearningBanner panelId="optimizer" />
      <SelectField
        label="Objective"
        value={objective}
        options={OptimizerObjectiveSchema.options.map((value) => ({ value, label: value.replace(/-/g, ' ') }))}
        onChange={(value) => setObjective(value as (typeof OptimizerObjectiveSchema.options)[number])}
        hint="The numerical score the search tries to improve. Results must still satisfy mission constraints."
      />

      <div>
        <p className="mb-1 text-xs text-muted">
          Variables to search over · <TermHelp termId="optimization">what optimization means</TermHelp>
        </p>
        <p className="mb-2 text-[11px] leading-relaxed text-faint">
          Selecting more variables expands the search space and can require more evaluations.
        </p>
        <div className="grid grid-cols-2 gap-1">
          {OptimizerVariableSchema.options.map((variable) => (
            <CheckboxField
              key={variable}
              label={VARIABLE_LABELS[variable]}
              checked={variables.has(variable)}
              onChange={(checked) => toggleVariable(variable, checked)}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={() => void handleRun()}
        disabled={variables.size === 0 || running}
        className="flex items-center justify-center gap-2 rounded-control border border-border bg-surface-raised px-3 py-1.5 text-xs text-text hover:bg-surface-sunken disabled:opacity-40"
      >
        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
        {running ? `Optimizing… ${Math.round((job.data?.progress ?? 0) * 100)}%` : 'Run optimizer'}
      </button>

      {job.data && (
        <>
          <div className="h-40 rounded-panel border border-border bg-surface-raised p-1">
            <ConvergenceChart values={job.data.convergence} />
          </div>

          {job.data.status === 'complete' && job.data.bestParameters && (
            <div className="rounded-panel border border-border bg-surface-raised p-3 text-xs">
              <p className="mb-1 font-medium text-text">Best parameters ({job.data.evaluations} evaluations)</p>
              <div className="grid grid-cols-2 gap-1 text-muted">
                {Object.entries(job.data.bestParameters).map(([key, value]) => (
                  <span key={key} className="contents">
                    <span>{VARIABLE_LABELS[key as OptimizerVariable] ?? key}</span>
                    <span className="text-right text-text">{value.toFixed(2)}</span>
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={handleApply}
                disabled={!job.data.resultingProgram}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-control border border-border px-3 py-1.5 text-text hover:bg-surface-sunken disabled:opacity-40"
              >
                <Wand2 className="h-3.5 w-3.5" /> Apply to mission
              </button>
            </div>
          )}

          {job.data.status === 'failed' && <p className="text-xs text-danger">{job.data.error}</p>}
        </>
      )}
      {job.isError && (
        <p className="text-xs text-danger">
          {job.error instanceof Error ? job.error.message : String(job.error)}
        </p>
      )}
    </div>
  );
}
